import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Eye,
  ArrowLeftRight,
  TrendingUp,
  Wallet,
  Bell,
  Settings as SettingsIcon,
  Sliders,
  FlaskConical,
  Zap,
  Bot,
  LogOut,
  Menu,
  X,
  ChevronDown,
} from 'lucide-react';
import { useStrategy } from '../StrategyContext';
import '../styles/layout.css';

interface AppLayoutProps {
  userEmail: string;
  onSignOut: () => void;
  isAdmin?: boolean;
}

const getNavItems = (isAdmin: boolean) => [
  {
    section: 'TradeBot',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/trades', label: 'Trades', icon: ArrowLeftRight },
      { to: '/performance', label: 'Performance', icon: TrendingUp },
      { to: '/bot', label: 'Bot Control', icon: Bot },
    ],
  },
  {
    section: 'TradeBot Strategy',
    items: [
      { to: '/strategy', label: 'Strategy', icon: Sliders },
    ],
  },
  {
    section: 'Trade Signals',
    items: [
      { to: '/watchlist', label: 'Watchlist', icon: Eye },
      { to: '/alerts', label: 'Alerts', icon: Bell },
    ],
  },
  {
    section: 'Portfolio',
    items: [
      { to: '/portfolio', label: 'Portfolio', icon: Wallet },
    ],
  },
  ...(isAdmin ? [{
    section: 'Configuration',
    items: [
      { to: '/backtest', label: 'Backtest', icon: FlaskConical },
      { to: '/optimizer', label: 'Optimizer', icon: Zap },
    ],
  }] : []),
  {
    section: 'Account',
    items: [
      { to: '/settings', label: 'Settings', icon: SettingsIcon },
    ],
  },
];

const AppLayout: React.FC<AppLayoutProps> = ({ userEmail, onSignOut, isAdmin = false }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { strategy, setStrategy, strategyLabel } = useStrategy();
  const navItems = getNavItems(isAdmin);

  return (
    <div style={{ minHeight: '100vh', background: '#0f1117', color: '#e4e4e7' }}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-logo" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="28" height="28" viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
            <rect width="32" height="32" rx="6" fill="#0f1117" stroke="#2a2d35" strokeWidth="1"/>
            <polyline points="4,24 10,18 16,20 28,6" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            <polyline points="22,6 28,6 28,12" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div>
            TradeSavvy
            <span>{strategyLabel}</span>
          </div>
        </div>

        {/* Strategy Switcher */}
        <div style={{ padding: '0 16px 8px' }}>
          <div style={{
            display: 'flex', gap: 4, background: '#1a1d25', borderRadius: 8, padding: 3,
          }}>
            <button
              onClick={() => setStrategy('donchian')}
              style={{
                flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 600,
                border: 'none', borderRadius: 6, cursor: 'pointer',
                background: strategy === 'donchian' ? '#22c55e' : 'transparent',
                color: strategy === 'donchian' ? '#000' : '#71717a',
                transition: 'all 0.15s',
              }}
            >
              Donchian
            </button>
            <button
              onClick={() => setStrategy('ualgo')}
              style={{
                flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 600,
                border: 'none', borderRadius: 6, cursor: 'pointer',
                background: strategy === 'ualgo' ? '#8b5cf6' : 'transparent',
                color: strategy === 'ualgo' ? '#fff' : '#71717a',
                transition: 'all 0.15s',
              }}
            >
              UAlgo
            </button>
          </div>
        </div>

        {navItems.map((section) => (
          <div key={section.section} className="nav-section">
            <div className="nav-label">{section.section}</div>
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = item.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.to);

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="nav-icon" size={18} />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        ))}

        <div className="sidebar-footer">
          <div className="user-info">
            <strong>{userEmail}</strong>
            <span style={{ color: '#eab308' }}>Paper Trading</span>
          </div>
          <button
            onClick={onSignOut}
            style={{
              marginTop: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: 'none',
              color: '#71717a',
              fontSize: 13,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>
      </div>

      {/* Mode Banner */}
      <div className="mode-banner paper">
        <button className="hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <div className="mode-dot" />
        <span>PAPER TRADING MODE</span>
        <span className="mode-desc">— simulated trades, no real money at risk</span>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <Outlet />
      </div>
    </div>
  );
};

export default AppLayout;
